import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi, demoApi, ApiError } from '@/lib/api';
import { Input, Textarea } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Mail, CheckCircle } from 'lucide-react';

const IS_SAAS = import.meta.env.VITE_DEPLOYMENT_MODE === 'saas';

// ── Request Demo Form (SaaS mode) ─────────────────────────────────────────────

function RequestDemoForm() {
  const [fullName,     setFullName]     = useState('');
  const [companyName,  setCompanyName]  = useState('');
  const [email,        setEmail]        = useState('');
  const [phone,        setPhone]        = useState('');
  const [message,      setMessage]      = useState('');
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [done,         setDone]         = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await demoApi.requestDemo({
        full_name:    fullName,
        company_name: companyName,
        email,
        phone:   phone   || undefined,
        message: message || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ background: '#1e3a5f' }}>
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src="/logo.png?v=4" alt="VantixMT"
                 className="w-40 sm:w-56 object-contain drop-shadow-xl" />
          </div>
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-12 text-center">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                <CheckCircle size={32} className="text-green-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Thanks — we'll be in touch!</h2>
            <p className="text-slate-500 text-sm mb-8">
              We typically respond within 1 business day.
            </p>
            <Link to="/login" className="text-brand-600 hover:underline text-sm font-medium">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: '#1e3a5f' }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png?v=4" alt="VantixMT"
               className="w-40 sm:w-56 object-contain drop-shadow-xl" />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl px-8 py-10">
          <div className="mb-6 text-center">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Request a demo</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label="Full Name *"
              type="text"
              placeholder="Jane Smith"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              required
              autoFocus
            />
            <Input
              label="Company Name *"
              type="text"
              placeholder="Acme Manufacturing"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              required
            />
            <Input
              label="Work Email *"
              type="email"
              placeholder="jane@acme.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <Input
              label="Phone"
              type="tel"
              placeholder="555-123-4567"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
            <Textarea
              label="Tell us about your operation"
              rows={3}
              placeholder="Number of machines, shifts, current process..."
              value={message}
              onChange={e => setMessage(e.target.value)}
            />

            {error && (
              <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>
            )}

            <Button type="submit" className="w-full mt-2" disabled={loading}>
              {loading ? 'Sending…' : 'Request a Demo'}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-600 hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Real Registration Form (local mode / needsSetup) ──────────────────────────

function AccountCreationForm() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [fullName,    setFullName]    = useState('');
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [error,       setError]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [done,        setDone]        = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.register(companyName, fullName, email, password);
      if (res.requires_verification) {
        setDone(true);
      } else {
        navigate('/login');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ background: '#1e3a5f' }}>
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src="/logo.png?v=4" alt="VantixMT"
                 className="w-40 sm:w-56 object-contain drop-shadow-xl" />
          </div>
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-12 text-center">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-full bg-brand-50 flex items-center justify-center">
                <Mail size={32} className="text-brand-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Check your inbox</h2>
            <p className="text-slate-500 text-sm mb-1">We sent a verification link to</p>
            <p className="font-semibold text-slate-800 mb-5">{email}</p>
            <p className="text-slate-500 text-sm mb-8">
              Click the link in the email to activate your account. The link expires in 24 hours.
            </p>
            <Link to="/login" className="text-brand-600 hover:underline text-sm font-medium">
              Back to sign in
            </Link>
          </div>
          <p className="text-center text-xs text-brand-200/80 mt-4">
            Didn't receive it? Check your spam folder.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: '#1e3a5f' }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png?v=4" alt="VantixMT"
               className="w-40 sm:w-56 object-contain drop-shadow-xl" />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl px-8 py-10">
          <div className="mb-6 text-center">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Create your company account</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label="Company Name *"
              type="text"
              placeholder="Acme Manufacturing"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              required
              autoFocus
            />
            <Input
              label="Your Full Name *"
              type="text"
              placeholder="Jane Smith"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              required
            />
            <Input
              label="Work Email *"
              type="email"
              placeholder="jane@acme.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <Input
              label="Password *"
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            <Input
              label="Confirm Password *"
              type="password"
              placeholder="••••••••"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
            />

            {error && (
              <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>
            )}

            <Button type="submit" className="w-full mt-2" disabled={loading}>
              {loading ? 'Creating account…' : 'Create account'}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-600 hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-brand-200/80 mt-4">
          Work email recommended — personal email also works · You'll be set up as administrator
        </p>
      </div>
    </div>
  );
}

// ── Public export — branches on mode ─────────────────────────────────────────

interface RegisterPageProps {
  /** When true, force the demo-request form (currently unused — kept for
   *  future "/demo" route or referral-link campaigns). */
  showDemoRequest?: boolean;
}

export function RegisterPage({ showDemoRequest = false }: RegisterPageProps) {
  // SaaS uses 30-day self-serve trial (no card required) — see
  // auth.ts:159 where demo_ends_at is set. Customers go straight to
  // account creation; Stripe checkout only kicks in when the trial ends.
  // The RequestDemoForm above is retained for high-touch enterprise leads
  // that we want to capture without auto-provisioning a tenant.
  if (showDemoRequest && IS_SAAS) {
    return <RequestDemoForm />;
  }
  return <AccountCreationForm />;
}
